import React from 'react';
import { ISparkDeposit } from '../../../public/APIContracts/APIContracts';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import './spark-deposit-modal.scss';

interface SparkDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  deposit: ISparkDeposit | null;
}

const SparkDepositModal = ({
  isOpen,
  onClose,
  deposit,
}: SparkDepositModalProps) => {
  if (!deposit) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysOverdue = (dueDateString: string) => {
    const dueDate = new Date(dueDateString);
    const today = new Date();
    const diffTime: number = today.getTime() - dueDate.getTime();
    const diffDays: number = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '#22c55e'; // green.
      case 'payment overdue':
        return '#ef4444'; // red.
      default:
        return '#f59e0b'; // orange/amber for pending, in progress, etc.
    }
  };

  const daysOverdue: number = getDaysOverdue(deposit.due_date);
  const isOverdue: boolean = deposit.status.toLowerCase() === 'payment overdue';
  const isPaid: boolean = deposit.status.toLowerCase() === 'paid';
  const remainingBalance: number = deposit.owed_amount;

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={deposit.name}
      description={`Deposit details for ${deposit.applied_to}`}
    >
      <div className="spark-deposit-modal">
        {/* Status Section. */}
        <div className="modal-section">
          <div className="status-header">
            <CategoryPill
              text={deposit.status}
              color={getStatusColor(deposit.status)}
              textColor="white"
            />
            {isOverdue && daysOverdue > 0 && (
              <CategoryPill
                text={`${daysOverdue} days overdue`}
                color="#dc2626" // darker red for urgency
                textColor="white"
              />
            )}
          </div>
        </div>
        {/* Financial Information. */}
        <div className="modal-section">
          <h3 className="section-title">Details</h3>
          <div className="financial-grid">
            <div className="financial-item">
              <label>Total Amount</label>
              <span className="amount-value">
                {formatCurrency(deposit.total_amount)}
              </span>
            </div>
            <div className="financial-item">
              <label>Amount Paid</label>
              <span
                className={`amount-paid ${deposit.status === 'Paid' ? 'has-payment' : 'no-payment'}`}
              >
                {formatCurrency(deposit.paid_amount)}
              </span>
            </div>
            <div className="financial-item">
              <label>Remaining Balance</label>
              <span
                className={`amount-remaining ${isPaid ? 'paid' : 'unpaid'}`}
              >
                {formatCurrency(remainingBalance)}
              </span>
            </div>
            <div className="financial-item">
              <label>Payment Progress</label>
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className={`progress-fill ${deposit.status === 'Paid' ? 'has-progress' : 'no-progress'}`}
                    style={{ width: `${deposit.percentage_paid}%` }}
                  ></div>
                </div>
                <span
                  className={`progress-text ${deposit.status === 'Paid' ? 'has-progress' : 'no-progress'}`}
                >
                  {deposit.percentage_paid}% paid
                </span>
              </div>
            </div>
            <div className="financial-grid">
              <div className="date-item">
                <label>Due Date</label>
                <span className={isOverdue ? 'overdue-date' : ''}>
                  {formatDate(deposit.due_date)}
                </span>
              </div>
            </div>
            <div className="date-item">
              <label>Last Updated</label>
              <span>{formatDateTime(deposit.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </GenericDialogue>
  );
};

export default SparkDepositModal;
